import { useMutation } from '@apollo/client';
import { faFileLines, faPlus, faRotateLeft, faWandMagicSparkles, faXmark } from '@fortawesome/pro-regular-svg-icons';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GENERATE_TEMPLATE_WITH_AI } from './queries.ts';
import { FIELD_FRAME_CLASS, FOCUS_CLASS, TAG_CLASS, VARIABLE_COLOR_CLASS } from './templateStyles.ts';
import type { NewTargetField } from './templateVariables.ts';
import Alert from '../../../core/components/Alert.tsx';
import Button from '../../../core/components/Button.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import classnames from '../../../core/utils/classnames.tsx';
import FieldsConfig from '../../../document/components/FieldsConfig.tsx';
import { PROMPT_CHIP_CLASS } from '../../../products/voice_agents/components/VoiceAgentPromptEditor.tsx';
import type { TemplateFormat, TypeDefinition } from '../OutputMapperEditor/types.ts';

const WandIcon = withIcon(faWandMagicSparkles);
const UndoIcon = withIcon(faRotateLeft);
const FileIcon = withIcon(faFileLines);
const PlusIcon = withIcon(faPlus);
const RemoveIcon = withIcon(faXmark);

// Same limit as the backend (template_ai.MAX_EXAMPLE_LENGTH), checked here so a too-large file fails fast.
const MAX_EXAMPLE_CHARS = 200_000;
const ACCEPTED_FILES = '.xml,.csv,.txt,.edi,.dat,text/xml,application/xml,text/csv,text/plain';
const FORMAT_BY_EXTENSION: Record<string, TemplateFormat> = { xml: 'xml', csv: 'csv', txt: 'txt' };

// Ready-made instructions, so the first use of the assistant does not start from an empty field.
const CREATE_EXAMPLE_KEYS = ['fromExample', 'skipEmpty', 'positionsInTens', 'semicolon'] as const;
// With a template in the editor the assistant edits it, so the suggestions are edits.
const EDIT_EXAMPLE_KEYS = ['skipEmpty', 'positionsInTens', 'addComments', 'matchExample'] as const;

interface ExampleFile {
    name: string;
    content: string;
}

export interface TemplateAiAssistantProps {
    template: string;
    format: TemplateFormat;
    /** The mapping targets in the `parent[].child` form — the only variables the AI may use. */
    targets: string[];
    /** The mapping's input types and loop sources: the AI may add target fields that read from them. */
    inputTypes: TypeDefinition[];
    collectionSources: Record<string, string>;
    /** Apply a suggestion: the new template plus the target fields it needs. */
    onApply: (template: string, newTargetFields: NewTargetField[]) => void;
    /** Restore the template and target fields from before the last suggestion. */
    onUndo: () => void;
    /** Called when the uploaded example is clearly another format than the configured one. */
    onFormatChange: (format: TemplateFormat) => void;
    /** Reports a running generation, so the panel can lock the editor meanwhile. */
    onLoadingChange?: (loading: boolean) => void;
    /** An instruction to put into the field (e.g. "add the missing target fields"); a new id sets it again. */
    presetInstruction?: { text: string; id: number } | null;
}

/**
 * "Create with AI" / "Edit with AI" for the Template tab, laid out like a field's instructions in the field
 * configuration: the instruction in a framed field, the example file and the action in the grey bar below it.
 * An existing template is always sent along and edited; the result replaces the editor content (with undo) and is
 * only stored when the user saves the configuration.
 */
const TemplateAiAssistant = ({
    template,
    format,
    targets,
    inputTypes,
    collectionSources,
    onApply,
    onUndo,
    onFormatChange,
    onLoadingChange,
    presetInstruction,
}: TemplateAiAssistantProps) => {
    const { t } = useTranslation('integrations');
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [instruction, setInstruction] = useState('');
    const [example, setExample] = useState<ExampleFile | null>(null);
    const [fileError, setFileError] = useState<string | null>(null);
    const [result, setResult] = useState<{ summary: string; unmatched: string[]; addedTargets: string[] } | null>(null);
    const [generationError, setGenerationError] = useState<string | null>(null);

    // the suggestion is never read back from the cache — it goes straight into the editor
    const [generate, { loading }] = useMutation(GENERATE_TEMPLATE_WITH_AI, { fetchPolicy: 'no-cache' });

    const isEditing = !!template.trim();
    const canGenerate = !loading && (!!instruction.trim() || !!example);
    const exampleKeys = isEditing ? EDIT_EXAMPLE_KEYS : CREATE_EXAMPLE_KEYS;

    useEffect(() => {
        onLoadingChange?.(loading);
    }, [loading, onLoadingChange]);

    useEffect(() => {
        if (presetInstruction) setInstruction(presetInstruction.text);
    }, [presetInstruction]);

    const handleFile = async (file: File | undefined) => {
        setFileError(null);
        if (!file) return;
        // a UTF-8 character takes at most 4 bytes, so a larger file is rejected without reading it
        const content = file.size > MAX_EXAMPLE_CHARS * 4 ? null : await file.text();
        if (content === null || content.length > MAX_EXAMPLE_CHARS) {
            setFileError(t('outputMapper.templateAi.fileTooLarge', 'The example file must not be larger than 200 KB.'));
            return;
        }
        setExample({ name: file.name, content });
        const extensionFormat = FORMAT_BY_EXTENSION[file.name.split('.').pop()?.toLowerCase() ?? ''];
        if (extensionFormat && extensionFormat !== format) onFormatChange(extensionFormat);
    };

    const handleGenerate = async () => {
        if (!canGenerate) return;
        setResult(null);
        setGenerationError(null);
        const { data } = await generate({
            variables: {
                templateFormat: format,
                instruction: instruction.trim() || null,
                example: example?.content ?? null,
                currentTemplate: template || null,
                targets,
                inputTypes,
                collectionSources,
            },
        }).catch((error) => {
            setGenerationError(error.message);
            return { data: null };
        });

        const payload = data?.generateTemplateWithAi;
        if (!payload) return;
        if (!payload.success || typeof payload.template !== 'string') {
            setGenerationError(
                payload.error || t('outputMapper.templateAi.error', 'The template could not be generated.')
            );
            return;
        }
        const newTargetFields: NewTargetField[] = payload.newTargetFields ?? [];
        setResult({
            summary: payload.summary || '',
            unmatched: payload.unmatched ?? [],
            addedTargets: newTargetFields.map((field) => field.target),
        });
        onApply(payload.template, newTargetFields);
    };

    const handleUndo = () => {
        if (!result) return;
        onUndo();
        setResult(null);
    };

    return (
        <FieldsConfig.Section
            title={
                isEditing
                    ? t('outputMapper.templateAi.editTitle', 'Edit with AI')
                    : t('outputMapper.templateAi.title', 'Create with AI')
            }
            description={
                isEditing
                    ? t(
                          'outputMapper.templateAi.editHint',
                          'Describe the change, or add an example file to restructure the template. The AI edits the current template, keeps everything you do not mention and can add target fields.'
                      )
                    : t(
                          'outputMapper.templateAi.hint',
                          'Add an example of the file the customer expects (XML, CSV or text) or describe the template. The AI keeps the example’s structure, fills in the target fields and adds missing ones from the document data.'
                      )
            }
        >
            <div className="flex flex-col gap-1.5">
                <div className={FIELD_FRAME_CLASS}>
                    <textarea
                        value={instruction}
                        disabled={loading}
                        onChange={(event) => setInstruction(event.target.value)}
                        onKeyDown={(event) => {
                            // Enter sends, Shift+Enter keeps the newline — the field is a one-off instruction
                            if (event.key === 'Enter' && !event.shiftKey) {
                                event.preventDefault();
                                handleGenerate();
                            }
                        }}
                        placeholder={
                            isEditing
                                ? t(
                                      'outputMapper.templateAi.editPlaceholder',
                                      'Describe the change, e.g. "Add the customer\'s order reference to the header"'
                                  )
                                : t(
                                      'outputMapper.templateAi.placeholder',
                                      'Optional: describe what the template should do, e.g. "Leave out empty elements"'
                                  )
                        }
                        className="min-h-16 resize-y rounded-t border-0 bg-transparent px-2 py-1.5 text-sm text-content-strong outline-none placeholder:text-content-weak"
                    />
                    <div className="flex flex-wrap items-center gap-2 rounded-b border-0 border-t border-solid border-line-weak bg-surface-medium px-3 py-2">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept={ACCEPTED_FILES}
                            className="hidden"
                            onChange={(event) => {
                                handleFile(event.target.files?.[0]);
                                event.target.value = '';
                            }}
                        />
                        {example ? (
                            <span className={classnames(TAG_CLASS, VARIABLE_COLOR_CLASS.loop)}>
                                <FileIcon className="text-[0.625rem]" />
                                <span className="max-w-64 truncate">{example.name}</span>
                                <button
                                    type="button"
                                    disabled={loading}
                                    aria-label={t('outputMapper.templateAi.removeFile', 'Remove example file')}
                                    title={t('outputMapper.templateAi.removeFile', 'Remove example file')}
                                    onClick={() => setExample(null)}
                                    className={classnames(
                                        'flex cursor-pointer items-center rounded border-0 bg-transparent p-0.5 text-current opacity-60 transition-opacity hover:opacity-100',
                                        FOCUS_CLASS
                                    )}
                                >
                                    <RemoveIcon className="text-[0.625rem]" />
                                </button>
                            </span>
                        ) : (
                            <button
                                type="button"
                                disabled={loading}
                                onClick={() => fileInputRef.current?.click()}
                                className={classnames(
                                    'inline-flex cursor-pointer items-center gap-1 rounded border border-dashed border-line-default bg-transparent px-1.5 py-0.5 text-xs font-medium text-content-medium transition-colors hover:bg-surface-strong hover:text-content-strong disabled:cursor-not-allowed disabled:opacity-50',
                                    FOCUS_CLASS
                                )}
                            >
                                <PlusIcon className="text-[0.625rem]" />
                                {t('outputMapper.templateAi.uploadExample', 'Example file')}
                            </button>
                        )}
                        {fileError && <span className="text-xs text-content-danger-strong">{fileError}</span>}
                        <span aria-hidden className="flex-1" />
                        {result && (
                            <Button
                                type="button"
                                variant="ghost"
                                className="flex items-center gap-2"
                                disabled={loading}
                                onClick={handleUndo}
                            >
                                <UndoIcon />
                                {t('outputMapper.templateAi.undo', 'Undo')}
                            </Button>
                        )}
                        <Button
                            type="button"
                            variant="primary"
                            className="flex items-center gap-2"
                            loading={loading}
                            disabled={!canGenerate}
                            onClick={handleGenerate}
                        >
                            <WandIcon />
                            {isEditing
                                ? t('outputMapper.templateAi.editButton', 'Edit template')
                                : t('outputMapper.templateAi.generateButton', 'Create template')}
                        </Button>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-1.5 text-xs text-content-medium">
                    <span>{t('outputMapper.templateAi.suggestions', 'Suggestions:')}</span>
                    {exampleKeys.map((key) => {
                        const exampleInstruction = t(`outputMapper.templateAi.examples.${key}`);
                        return (
                            <button
                                key={key}
                                type="button"
                                disabled={loading}
                                className={classnames(
                                    PROMPT_CHIP_CLASS,
                                    VARIABLE_COLOR_CLASS.variable,
                                    'disabled:cursor-not-allowed disabled:opacity-50'
                                )}
                                onClick={() => setInstruction(exampleInstruction)}
                            >
                                {exampleInstruction}
                            </button>
                        );
                    })}
                </div>
            </div>

            {generationError && <Alert severity="error">{generationError}</Alert>}
            {result && (
                <Alert severity="success">
                    <div className="flex flex-col gap-1">
                        <span className="whitespace-pre-line">
                            {result.summary || t('outputMapper.templateAi.applied', 'Template created.')}
                        </span>
                        {result.addedTargets.length > 0 && (
                            <span className="text-xs">
                                {t('outputMapper.templateAi.addedTargets', 'Added target fields:')}{' '}
                                <span className="font-mono">{result.addedTargets.join(', ')}</span>
                            </span>
                        )}
                        {result.unmatched.length > 0 && (
                            <span className="text-xs">
                                {t('outputMapper.templateAi.unmatched', 'No matching variable for:')}{' '}
                                <span className="font-mono">{result.unmatched.join(', ')}</span>
                            </span>
                        )}
                        <span className="text-xs">
                            {t(
                                'outputMapper.templateAi.reviewNote',
                                'Test the template and save the configuration to keep it.'
                            )}
                        </span>
                    </div>
                </Alert>
            )}
        </FieldsConfig.Section>
    );
};

export default TemplateAiAssistant;
